import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getGraphQLClient, GET_BOOK_SETUP_STATUS } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';

interface BookSetupCardProps {
  bookId: string;
  onOpenVoice: () => void;
}

interface SetupStatus {
  hasCover: boolean;
  hasVoice: boolean;
  hasAudience: boolean;
  hasGithubSync: boolean;
  hasSummary: boolean;
}

type SetupResponse = {
  book: {
    id: string;
    title: string | null;
    summary: string | null;
    writingStyle: string | null;
    coverImageUrl: string | null;
    readerAgeFrom: number | null;
    readerAgeTo: number | null;
    audienceReviewed: boolean;
    githubSyncConfigured: boolean;
  } | null;
};

const DISMISS_KEY_PREFIX = 'bookSetupCard:dismissed:';

function isDismissed(bookId: string): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY_PREFIX + bookId) === '1';
  } catch {
    return false;
  }
}

function setDismissed(bookId: string) {
  try {
    localStorage.setItem(DISMISS_KEY_PREFIX + bookId, '1');
  } catch {
    /* ignore */
  }
}

export default function BookSetupCard({ bookId, onOpenVoice }: BookSetupCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [dismissed, setDismissedState] = useState<boolean>(() => isDismissed(bookId));

  useEffect(() => {
    if (dismissed) return;

    let cancelled = false;
    const client = getGraphQLClient();
    const result = client.request<SetupResponse>(GET_BOOK_SETUP_STATUS, { id: bookId });
    if (!result || typeof (result as Promise<SetupResponse>).then !== 'function') return;
    (result as Promise<SetupResponse>)
      .then((data) => {
        if (cancelled || !data || !data.book) return;
        setStatus({
          hasCover: !!data.book.coverImageUrl,
          hasVoice: !!data.book.writingStyle?.trim(),
          hasAudience: data.book.audienceReviewed,
          hasGithubSync: data.book.githubSyncConfigured,
          hasSummary: !!data.book.summary?.trim(),
        });
      })
      .catch((err) => captureException(err));

    return () => {
      cancelled = true;
    };
  }, [bookId, dismissed]);

  if (dismissed || !status) return null;

  const items: Array<{
    key: keyof SetupStatus;
    label: string;
    description: string;
    action: () => void;
    cta: string;
  }> = [
    {
      key: 'hasCover',
      label: t('bookSetup.coverLabel', 'Add a cover image'),
      description: t('bookSetup.coverDescription', 'Give your book a face readers will remember.'),
      action: () => navigate(`/books/${bookId}/cover-editor`),
      cta: t('bookSetup.coverCta', 'Add cover'),
    },
    {
      key: 'hasVoice',
      label: t('bookSetup.voiceLabel', 'Define the writing voice'),
      description: t(
        'bookSetup.voiceDescription',
        'Tell the AI how this book should sound and read.'
      ),
      action: onOpenVoice,
      cta: t('bookSetup.voiceCta', 'Set voice'),
    },
    {
      key: 'hasAudience',
      label: t('bookSetup.audienceLabel', 'Set your target audience'),
      description: t(
        'bookSetup.audienceDescription',
        'Reader age helps the AI calibrate tone and vocabulary.'
      ),
      action: () => navigate(`/books/${bookId}/edit/audience`),
      cta: t('bookSetup.audienceCta', 'Set audience'),
    },
    {
      key: 'hasGithubSync',
      label: t('bookSetup.githubLabel', 'Sync with GitHub'),
      description: t(
        'bookSetup.githubDescription',
        'Optional. Keep prose in a git repo for version history and external review.'
      ),
      action: () => navigate(`/books/${bookId}/sync`),
      cta: t('bookSetup.githubCta', 'Connect'),
    },
  ];

  const incomplete = items.filter((item) => !status[item.key]);

  // Nothing left to suggest — hide the card.
  if (incomplete.length === 0) return null;

  const handleDismiss = () => {
    setDismissed(bookId);
    setDismissedState(true);
  };

  return (
    <AnimatePresence>
      <motion.section
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className="mb-4 rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-br from-indigo-50/70 to-white dark:from-indigo-950/30 dark:to-gray-900 p-4 sm:p-5"
        aria-label={t('bookSetup.title', 'Make this book yours')}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('bookSetup.title', 'Make this book yours')}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {t(
                'bookSetup.subtitle',
                'Optional touches that help the AI write closer to your vision.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline whitespace-nowrap"
          >
            {t('bookSetup.dismiss', "Skip — I'll do this later")}
          </button>
        </div>

        <ul className="space-y-2">
          {incomplete.map((item) => (
            <li
              key={item.key}
              className="flex items-start gap-3 rounded-lg bg-white/70 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3 py-2"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 inline-block w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {item.description}
                </p>
              </div>
              <button
                type="button"
                onClick={item.action}
                className="shrink-0 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 hover:underline"
              >
                {item.cta} →
              </button>
            </li>
          ))}
        </ul>
      </motion.section>
    </AnimatePresence>
  );
}
